import { getTagColor, type SelectTag } from '@blocksuite/data-view';
import { nanoid, Text } from '@blocksuite/store';
import {
  convertDatabaseCellValue,
  DATABASE_CELL_CONVERSIONS,
  databaseTextOptionNames,
} from '@streamlain/database-identity';

type ColumnData = Record<string, unknown>;

type DatabasePropertyConvert = {
  convert: (
    property: any,
    cells: any[]
  ) => { property: ColumnData; cells: unknown[] };
  from: string;
  to: string;
};

const NUMBER_DATA = { decimal: 0, format: 'number' as const };
const OPTION_TYPES = new Set(['select', 'multi-select']);

/** A cell as the conversion rules read it: text as a string. */
const storedCell = (type: string, value: unknown) =>
  type === 'rich-text' && value != null ? value.toString() : value;

/** A converted value as the editor stores it: text as a Y.Text. */
const editorCell = (type: string, value: unknown) => {
  if (value === null) return undefined;
  return type === 'rich-text' ? new Text(String(value)).yText : value;
};

/** One option per distinct name the texts stand for, in table order. */
function optionsOfTexts(toType: string, cells: readonly unknown[]) {
  const names = new Set<string>();
  for (const cell of cells) {
    const text = storedCell('rich-text', cell);
    if (typeof text !== 'string') continue;
    for (const name of databaseTextOptionNames(toType, text)) names.add(name);
  }
  return {
    options: [...names].map(
      (value): SelectTag => ({
        id: nanoid(),
        value,
        color: getTagColor(),
      })
    ),
  };
}

/** The column data of the target type, built from the source column. */
function targetColumnData(
  fromType: string,
  toType: string,
  property: ColumnData,
  cells: readonly unknown[]
): ColumnData {
  if (fromType === 'rich-text' && OPTION_TYPES.has(toType))
    return optionsOfTexts(toType, cells);
  if (toType === 'number') return { ...NUMBER_DATA };
  if (OPTION_TYPES.has(fromType) && OPTION_TYPES.has(toType)) return property;
  return {};
}

/**
 * The editor's column-wide type change. The cells follow
 * `convertDatabaseCellValue`, the rule set every reader of a type-changed
 * column uses, so a converted column reads the same everywhere.
 */
export const databasePropertyConverts: DatabasePropertyConvert[] =
  DATABASE_CELL_CONVERSIONS.map(({ fromType, toType }) => ({
    from: fromType,
    to: toType,
    convert: (property, cells) => {
      const fromData: ColumnData = property ?? {};
      const toData = targetColumnData(fromType, toType, fromData, cells);
      return {
        property: toData,
        cells: cells.map(cell =>
          editorCell(
            toType,
            convertDatabaseCellValue({
              fromType,
              fromData,
              toType,
              toData,
              value: storedCell(fromType, cell),
            })
          )
        ),
      };
    },
  }));
